import React, { useRef } from 'react'
import { Printer, Download, Mail, CheckCircle2 } from 'lucide-react'
import { Transaction } from '@/types/pos'
import QRCode from 'qrcode'

interface ReceiptModalProps {
  transaction: Transaction
  storeName?: string
  storeAddress?: string
  storePhone?: string
  storeEmail?: string
  receiptFooter?: string
  onClose: () => void
  onPrint?: () => void
  onEmail?: () => void
  onNewTransaction?: () => void
}

export const ReceiptModal: React.FC<ReceiptModalProps> = ({
  transaction,
  storeName = 'Sachin Poth Madura',
  storeAddress = '',
  storePhone = '',
  storeEmail = '',
  receiptFooter = 'Thank you for your purchase!',
  onClose,
  onPrint,
  onEmail,
  onNewTransaction,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null)
  const [qrCodeUrl, setQrCodeUrl] = React.useState<string>('')

  // Generate QR code for receipt
  React.useEffect(() => {
    const generateQR = async () => {
      try {
        const receiptData = JSON.stringify({
          receipt: transaction.receiptNumber || transaction.transactionNumber,
          total: transaction.total,
          date: transaction.createdAt
        })
        const url = await QRCode.toDataURL(receiptData, { width: 80, margin: 1 })
        setQrCodeUrl(url)
      } catch (err) {
        console.error('QR generation failed:', err)
      }
    }
    generateQR()
  }, [transaction])

  // Consolidate variant items for compact receipts
  // Groups show: "A4 — 3 types, 6 pcs" as header, "Red x2, Blue x1, White x3" as detail
  const consolidateForReceipt = (items: any[]) => {
    const result: { name: string; detail: string | null; quantity: number; total: number; isGroup: boolean }[] = []
    const variantMap = new Map<string, any[]>()
    const standalone: any[] = []

    for (const item of items) {
      const gn = item.variantGroupName
      if (gn) {
        if (!variantMap.has(gn)) variantMap.set(gn, [])
        variantMap.get(gn)!.push(item)
      } else {
        standalone.push(item)
      }
    }

    for (const [groupName, groupItems] of variantMap) {
      if (groupItems.length === 1) {
        const item = groupItems[0]
        result.push({
          name: item.productName || item.product?.name || item.name,
          detail: null,
          quantity: item.quantity,
          total: Math.abs(item.total || (item.product?.price || item.unitPrice || 0) * item.quantity),
          isGroup: false,
        })
      } else {
        const totalQty = groupItems.reduce((s: number, i: any) => s + i.quantity, 0)
        const totalAmt = groupItems.reduce((s: number, i: any) => s + Math.abs(i.total || (i.product?.price || i.unitPrice || 0) * i.quantity), 0)
        const variantDetails = groupItems.map((i: any) => {
          const fullName: string = i.productName || i.product?.name || i.name
          let shortName = fullName
          if (fullName.startsWith(groupName)) {
            shortName = fullName.slice(groupName.length).replace(/^[\s\-–—:]+/, '').trim()
          }
          return `${shortName || fullName} x${i.quantity}`
        }).join(', ')
        result.push({
          name: `${groupName} — ${groupItems.length} types, ${totalQty} pcs`,
          detail: variantDetails,
          quantity: totalQty,
          total: totalAmt,
          isGroup: true,
        })
      }
    }

    for (const item of standalone) {
      result.push({
        name: item.productName || item.product?.name || item.name,
        detail: null,
        quantity: item.quantity,
        total: Math.abs(item.total || (item.product?.price || item.unitPrice || 0) * item.quantity),
        isGroup: false,
      })
    }

    return result
  }

  const handlePrint = () => {
    const printWindow = window.open('', '_blank')
    if (printWindow && receiptRef.current) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Receipt - ${transaction.receiptNumber || transaction.transactionNumber}</title>
            <style>
              @media print {
                @page { margin: 2mm 5mm; size: 80mm auto; }
                body { margin: 0; padding: 4mm 6mm; }
              }
              body {
                font-family: 'Arial', sans-serif;
                font-size: 12px;
                line-height: 1.4;
                max-width: 80mm;
                margin: 0 auto;
                padding: 4mm 6mm;
                color: #000;
              }
              .header { text-align: center; margin-bottom: 16px; }
              .header h1 { margin: 0; font-size: 18px; font-weight: bold; }
              .header p { margin: 4px 0; font-size: 10px; }
              .divider { border-top: 1px dashed #000; margin: 12px 0; }
              .item { display: flex; justify-content: space-between; margin: 6px 0; font-size: 11px; }
              .item-name { flex: 1; max-width: 60%; }
              .item-qty { margin: 0 8px; }
              .item-price { text-align: right; font-weight: bold; }
              .totals { margin-top: 12px; }
              .total-row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 11px; }
              .total-row.final { font-weight: bold; font-size: 14px; border-top: 2px solid #000; padding-top: 8px; margin-top: 8px; }
              .footer { text-align: center; margin-top: 20px; font-size: 10px; }
              .qr { text-align: center; margin-top: 12px; }
              .qr img { width: 60px; height: 60px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${storeName}</h1>
              ${storeAddress ? `<p>${storeAddress}</p>` : ''}
              ${storePhone ? `<p>Tel: ${storePhone}</p>` : ''}
              ${storeEmail ? `<p>Email: ${storeEmail}</p>` : ''}
              <p>${new Date().toLocaleString()}</p>
            </div>
            <div class="divider"></div>
            <div>
              <p><strong>Receipt #:</strong> ${transaction.receiptNumber || transaction.transactionNumber}</p>
              <p><strong>Date:</strong> ${new Date(transaction.createdAt).toLocaleString()}</p>
              ${transaction.customerName ? `<p><strong>Customer:</strong> ${transaction.customerName}</p>` : ''}
            </div>
            <div class="divider"></div>
            <div>
              ${consolidateForReceipt((transaction.items as any[]).filter((i: any) => !i.isReturn)).map(line => `
                <div class="item">
                  <span class="item-name">${line.name}</span>
                  <span class="item-qty">${line.isGroup ? '' : `x${line.quantity}`}</span>
                  <span class="item-price">Rs.${line.total.toFixed(2)}</span>
                </div>
                ${line.detail ? `<div style="font-size:9px;color:#666;padding-left:4px;margin:-2px 0 4px;">${line.detail}</div>` : ''}
              `).join('')}
              ${(transaction.items as any[]).some((i: any) => i.isReturn) ? `
                <div class="divider"></div>
                <div style="text-align:center;font-size:10px;font-weight:bold;letter-spacing:1px;margin:4px 0;">RETURNS</div>
                ${(transaction.items as any[]).filter((i: any) => i.isReturn).map((item: any) => `
                  <div class="item" style="color:#c00;">
                    <span class="item-name">${item.productName || item.product?.name || item.name}</span>
                    <span class="item-qty">x${item.quantity}</span>
                    <span class="item-price">-Rs.${Math.abs(item.total || (item.product?.price || item.unitPrice || 0) * item.quantity).toFixed(2)}</span>
                  </div>
                `).join('')}
              ` : ''}
            </div>
            <div class="divider"></div>
            <div class="totals">
              <div class="total-row">
                <span>Subtotal:</span>
                <span>Rs.${transaction.subtotal.toFixed(2)}</span>
              </div>
              ${transaction.discount > 0 ? (() => {
                const discType = (transaction as any).discountType || 'percentage'
                const discAmt = discType === 'fixed' ? transaction.discount : (Math.max(0, transaction.subtotal) * transaction.discount / 100)
                const discLabel = discType === 'fixed' ? 'Discount:' : `Discount (${transaction.discount}%):`
                return `<div class="total-row"><span>${discLabel}</span><span>-Rs.${discAmt.toFixed(2)}</span></div>`
              })() : ''}
              <div class="total-row final">
                <span>TOTAL:</span>
                <span>Rs.${transaction.total.toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span>Payment:</span>
                <span>${transaction.paymentMethod.toUpperCase()}</span>
              </div>
              ${(transaction as any).advanceApplied > 0 ? `
                <div class="total-row" style="color:#059669;">
                  <span>Advance applied:</span>
                  <span>Rs.${(transaction as any).advanceApplied.toFixed(2)}</span>
                </div>
              ` : ''}
              ${transaction.amountPaid != null ? `
                <div class="total-row">
                  <span>Amount paid (today):</span>
                  <span>Rs.${transaction.amountPaid.toFixed(2)}</span>
                </div>
              ` : ''}
              ${transaction.change && transaction.change > 0 ? `
                <div class="total-row">
                  <span>Change:</span>
                  <span>Rs.${transaction.change.toFixed(2)}</span>
                </div>
              ` : ''}
            </div>
            <div class="divider"></div>
            <div class="footer">
              <p>${receiptFooter}</p>
              ${qrCodeUrl ? `<div class="qr"><img src="${qrCodeUrl}" alt="Receipt QR" /></div>` : ''}
            </div>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 250)
    }
    onPrint?.()
  }

  const handleDownload = () => {
    const content = `
${storeName}
${storeAddress}
${storePhone ? `Tel: ${storePhone}` : ''}
${storeEmail ? `Email: ${storeEmail}` : ''}

Receipt #: ${transaction.receiptNumber || transaction.transactionNumber}
Date: ${new Date(transaction.createdAt).toLocaleString()}
${transaction.customerName ? `Customer: ${transaction.customerName}` : ''}

${'─'.repeat(40)}

${consolidateForReceipt((transaction.items as any[]).filter((i: any) => !i.isReturn)).map(line =>
      `${line.name.padEnd(25)} ${line.isGroup ? '   ' : `x${line.quantity}`}  Rs.${line.total.toFixed(2)}${line.detail ? `\n  ${line.detail}` : ''}`
    ).join('\n')}
${(transaction.items as any[]).filter((i: any) => i.isReturn).length > 0 ? `\nRETURNS:\n${(transaction.items as any[]).filter((i: any) => i.isReturn).map((item: any) =>
      `${(item.productName || item.product?.name || item.name).padEnd(25)} x${item.quantity}  -Rs.${Math.abs(item.total || (item.product?.price || item.unitPrice || 0) * item.quantity).toFixed(2)}`
    ).join('\n')}` : ''}

${'─'.repeat(40)}

Subtotal: Rs.${transaction.subtotal.toFixed(2)}
${(() => {
  if (transaction.discount <= 0) return ''
  const discType = (transaction as any).discountType || 'percentage'
  const discAmt = discType === 'fixed' ? transaction.discount : (Math.max(0, transaction.subtotal) * transaction.discount / 100)
  const discLabel = discType === 'fixed' ? 'Discount' : `Discount (${transaction.discount}%)`
  return `${discLabel}: -Rs.${discAmt.toFixed(2)}`
})()}
${'═'.repeat(40)}
TOTAL: Rs.${transaction.total.toFixed(2)}
${transaction.paymentMethod === 'split' && transaction.paymentSplits?.length
  ? `Split Payment:\n${transaction.paymentSplits.map(sp => `  ${sp.method.replace('_', ' ').toUpperCase()}: Rs.${sp.amount.toFixed(2)}`).join('\n')}`
  : `Payment Method: ${transaction.paymentMethod.toUpperCase()}`}
${(transaction as any).advanceApplied > 0 ? `Advance applied: Rs.${(transaction as any).advanceApplied.toFixed(2)}\n` : ''}${transaction.amountPaid != null ? `Amount paid (today): Rs.${transaction.amountPaid.toFixed(2)}\n` : ''}
${transaction.change && transaction.change > 0 ? `Change: Rs.${transaction.change.toFixed(2)}` : ''}

${'─'.repeat(40)}

${receiptFooter}
    `.trim()

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `receipt-${transaction.receiptNumber || transaction.transactionNumber}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const getPaymentMethodDisplay = (method: string) => {
    const methods: Record<string, { icon: string; label: string }> = {
      cash: { icon: '💵', label: 'Cash' },
      card: { icon: '💳', label: 'Card' },
      wallet: { icon: '📱', label: 'E-Wallet' },
      bank_transfer: { icon: '🏦', label: 'Bank Transfer' },
      split: { icon: '➗', label: 'Split Payment' },
    }
    return methods[method] || { icon: '💰', label: method }
  }

  const paymentDisplay = getPaymentMethodDisplay(transaction.paymentMethod)

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Success Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold esales-text">Transaction Complete!</h2>
          <p className="text-muted-foreground mt-1">Receipt #{transaction.receiptNumber || transaction.transactionNumber}</p>
        </div>

        {/* Receipt Preview */}
        <div ref={receiptRef} className="bg-white text-black p-6 rounded-lg text-sm mb-6 shadow-inner" style={{ fontFamily: 'Arial, sans-serif' }}>
          <div className="text-center mb-4">
            <h4 className="font-bold text-lg">{storeName}</h4>
            {storeAddress && <p className="text-xs">{storeAddress}</p>}
            {storePhone && <p className="text-xs">Tel: {storePhone}</p>}
            {storeEmail && <p className="text-xs">Email: {storeEmail}</p>}
            <p className="text-xs mt-2">{new Date(transaction.createdAt).toLocaleString()}</p>
          </div>
          
          <div className="border-t border-b border-gray-300 py-2 my-3">
            <p className="text-xs"><strong>Receipt #:</strong> {transaction.receiptNumber || transaction.transactionNumber}</p>
            {transaction.customerName && <p className="text-xs"><strong>Customer:</strong> {transaction.customerName}</p>}
          </div>

          <div className="my-4 space-y-1">
            {/* Purchase items — variants consolidated */}
            {consolidateForReceipt((transaction.items as any[]).filter((i: any) => !i.isReturn)).map((line, i) => (
              <div key={`item-${i}`}>
                <div className="flex justify-between text-xs">
                  <span className="flex-1 truncate pr-2">{line.name}</span>
                  {!line.isGroup && <span className="px-2">x{line.quantity}</span>}
                  <span className="font-bold">Rs.{line.total.toFixed(2)}</span>
                </div>
                {line.detail && (
                  <p className="text-[9px] text-gray-400 pl-1 -mt-0.5 mb-1">{line.detail}</p>
                )}
              </div>
            ))}
            {/* Return items */}
            {(transaction.items as any[]).some((i: any) => i.isReturn) && (
              <>
                <div className="flex items-center gap-1 my-2">
                  <div className="flex-1 border-t border-dashed border-gray-400" />
                  <span className="text-[9px] font-bold uppercase text-gray-500 tracking-wider">Returns</span>
                  <div className="flex-1 border-t border-dashed border-gray-400" />
                </div>
                {(transaction.items as any[]).filter((i: any) => i.isReturn).map((item: any, index: number) => (
                  <div key={`return-${index}`} className="flex justify-between text-xs text-red-600">
                    <span className="flex-1 truncate pr-2">
                      {item.productName || item.product?.name || item.name}
                      {item.returnFromReceiptNumber && (
                        <span className="text-[9px] text-gray-500 ml-1">(from #{item.returnFromReceiptNumber.length > 12 ? item.returnFromReceiptNumber.slice(0, 12) + '...' : item.returnFromReceiptNumber})</span>
                      )}
                    </span>
                    <span className="px-2">x{item.quantity}</span>
                    <span className="font-bold">-Rs.{Math.abs(item.total || (item.product?.price || item.unitPrice || 0) * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="border-t border-gray-300 pt-3 mt-3 space-y-1">
            {(() => {
              const returnCredit = (transaction.items as any[]).filter((i: any) => i.isReturn).reduce((sum: number, i: any) => sum + Math.abs(i.total || 0), 0)
              const purchaseSubtotal = (transaction.items as any[]).filter((i: any) => !i.isReturn).reduce((sum: number, i: any) => sum + Math.abs(i.total || 0), 0)
              return (
                <>
                  <div className="flex justify-between text-xs">
                    <span>Subtotal:</span>
                    <span>Rs.{purchaseSubtotal.toFixed(2)}</span>
                  </div>
                  {returnCredit > 0 && (
                    <div className="flex justify-between text-xs text-red-600 font-semibold">
                      <span>Return Credit:</span>
                      <span>-Rs.{returnCredit.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )
            })()}
            {transaction.discount > 0 && (() => {
              const discType = (transaction as any).discountType || 'percentage'
              const discAmt = discType === 'fixed' ? transaction.discount : (Math.max(0, transaction.subtotal) * transaction.discount / 100)
              const discLabel = discType === 'fixed' ? 'Discount:' : `Discount (${transaction.discount}%):`
              return (
                <div className="flex justify-between text-xs text-green-600">
                  <span>{discLabel}</span>
                  <span>-Rs.{discAmt.toFixed(2)}</span>
                </div>
              )
            })()}
            <div className="flex justify-between font-bold text-base border-t border-gray-300 pt-2 mt-2">
              <span>TOTAL:</span>
              <span>{transaction.total < 0 ? '-' : ''}Rs.{Math.abs(transaction.total).toFixed(2)}</span>
            </div>
            {transaction.paymentMethod === 'split' && transaction.paymentSplits?.length ? (
              <div className="mt-2 space-y-1">
                <div className="text-xs font-semibold">Split Payment:</div>
                {transaction.paymentSplits.map((sp, i) => {
                  const spDisplay = getPaymentMethodDisplay(sp.method)
                  return (
                    <div key={i} className="flex justify-between text-xs pl-2">
                      <span>{spDisplay.icon} {spDisplay.label}</span>
                      <span>Rs.{sp.amount.toFixed(2)}</span>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex justify-between text-xs mt-2">
                <span>Payment:</span>
                <span>{paymentDisplay.icon} {paymentDisplay.label}</span>
              </div>
            )}
            {(transaction as any).advanceApplied > 0 && (
              <div className="flex justify-between text-xs text-green-600">
                <span>Advance applied:</span>
                <span>Rs.{(transaction as any).advanceApplied.toFixed(2)}</span>
              </div>
            )}
            {transaction.amountPaid != null && (
              <div className="flex justify-between text-xs">
                <span>Amount paid (today):</span>
                <span>Rs.{transaction.amountPaid.toFixed(2)}</span>
              </div>
            )}
            {transaction.change && transaction.change > 0 && (
              <div className="flex justify-between text-xs font-bold text-blue-600">
                <span>Change Due:</span>
                <span>Rs.{transaction.change.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div className="text-center mt-6 border-t border-gray-300 pt-3">
            <p className="text-xs">{receiptFooter}</p>
            {qrCodeUrl && (
              <div className="mt-2 flex justify-center">
                <img src={qrCodeUrl} alt="Receipt QR" className="w-16 h-16" />
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <button
            onClick={handlePrint}
            className="flex flex-col items-center justify-center p-4 bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors"
          >
            <Printer className="w-6 h-6 text-primary mb-1" />
            <span className="text-xs font-semibold">Print</span>
          </button>
          <button
            onClick={handleDownload}
            className="flex flex-col items-center justify-center p-4 bg-blue-500/10 hover:bg-blue-500/20 rounded-xl transition-colors"
          >
            <Download className="w-6 h-6 text-blue-500 mb-1" />
            <span className="text-xs font-semibold">Download</span>
          </button>
          <button
            onClick={onEmail}
            className="flex flex-col items-center justify-center p-4 bg-purple-500/10 hover:bg-purple-500/20 rounded-xl transition-colors"
          >
            <Mail className="w-6 h-6 text-purple-500 mb-1" />
            <span className="text-xs font-semibold">Email</span>
          </button>
        </div>

        {/* Bottom Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => {
              onNewTransaction?.()
              onClose()
            }}
            className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm glow-effect transition-colors"
          >
            New Transaction
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReceiptModal
